import React, { useState } from 'react';
import { Settings, ChevronDown, ChevronRight } from 'lucide-react';
import type { ProblemType, UserSettings, ProblemGroupId } from '../../types';
import { PROBLEM_GROUPS, GROUP_ORDER, PROBLEM_DEFINITIONS } from '../../constants';

// Show groups in the learning-first order.
const ORDERED_GROUPS = GROUP_ORDER.map((id) => PROBLEM_GROUPS.find((g) => g.id === id)!).filter(Boolean);

interface ParentSettingsProps {
  settings: UserSettings;
  onToggle: (type: ProblemType) => void;
  onToggleGroup: (groupId: ProblemGroupId) => void;
  isGroupEnabled: (groupId: ProblemGroupId) => boolean;
  isGroupPartiallyEnabled: (groupId: ProblemGroupId) => boolean;
  onBack: () => void;
}

export const ParentSettings: React.FC<ParentSettingsProps> = ({
  settings,
  onToggle,
  onToggleGroup,
  isGroupEnabled,
  isGroupPartiallyEnabled,
  onBack,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<ProblemGroupId>>(
    new Set(PROBLEM_GROUPS.map((g) => g.id))
  );

  const toggleExpand = (groupId: ProblemGroupId) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  return (
    <div className="h-[100dvh] overflow-y-auto bg-gray-50 p-6 font-sans text-gray-800">
      {/* Header */}
      <header className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings /> 保護者用設定
        </h1>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 transition-colors"
        >
          お子様画面へ
        </button>
      </header>

      {/* Settings Card */}
      <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
        <h2 className="text-xl font-bold mb-4 border-b pb-2">ステージ管理</h2>
        <div className="space-y-4">
          {ORDERED_GROUPS.map((group) => {
            const isExpanded = expandedGroups.has(group.id);
            const groupEnabled = isGroupEnabled(group.id);
            const partiallyEnabled = isGroupPartiallyEnabled(group.id);

            return (
              <div key={group.id} className="border rounded-lg overflow-hidden">
                {/* Group Header */}
                <div
                  className="flex items-center justify-between p-4 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => toggleExpand(group.id)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{group.icon}</span>
                    <div>
                      <div className="font-bold" style={{ color: group.color }}>
                        {group.title}
                      </div>
                      <div className="text-xs text-gray-500">{group.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className="relative"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleGroup(group.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        className="w-6 h-6 cursor-pointer accent-blue-500"
                        checked={groupEnabled}
                        ref={(el) => {
                          if (el) el.indeterminate = partiallyEnabled;
                        }}
                        onChange={() => onToggleGroup(group.id)}
                      />
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </div>

                {/* Group Items */}
                {isExpanded && (
                  <div className="divide-y">
                    {group.types.map((key) => {
                      const def = PROBLEM_DEFINITIONS[key];
                      return (
                        <div
                          key={key}
                          className="flex justify-between items-center p-3 pl-12 bg-white hover:bg-gray-50"
                        >
                          <div>
                            <div
                              className="font-bold flex items-center gap-2"
                              style={{ color: def.color }}
                            >
                              {def.icon} {def.title}
                            </div>
                            <div className="text-xs text-gray-500">{def.description}</div>
                          </div>
                          <input
                            type="checkbox"
                            className="w-6 h-6 cursor-pointer accent-blue-500"
                            checked={settings.enabledTypes[key]}
                            onChange={() => onToggle(key)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
